<template>
  <div class="vehicle-details-editor">
    <v-row density="comfortable">
      <v-col
        v-for="field in fields"
        :key="field.key"
        cols="12"
        sm="6"
      >
        <v-text-field
          v-model="form[field.key]"
          :label="field.label"
          density="compact"
          hide-details
          variant="outlined"
        />
      </v-col>
    </v-row>

    <div class="d-flex justify-end ga-2 mt-3">
      <v-btn :disabled="saving" size="small" variant="text" @click="$emit('close')">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn :loading="saving" color="primary" size="small" variant="tonal" @click="save">
        {{ $t('save') }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {reactive, ref} from 'vue';
import axios from 'axios';
import {push} from 'notivue';
import type {OrderLineVehicleDetails} from '@/types/order';

type VehicleDetails = Record<string, string>;

const props = defineProps<{
  documentUuid: string;
  lineUuid: string;
  details: OrderLineVehicleDetails | null | undefined;
}>();

const emit = defineEmits<{
  saved: [details: VehicleDetails];
  close: [];
}>();

// Fixed field set — matches the server-side whitelist so the form and the API
// agree on exactly which keys a line can carry.
const fields = [
  {key: 'make', label: 'Make'},
  {key: 'model', label: 'Model'},
  {key: 'year', label: 'Year'},
  {key: 'type', label: 'Type'},
  {key: 'vin', label: 'VIN'},
  {key: 'license_no', label: 'License No'},
  {key: 'pickup_location', label: 'Pickup Location'},
  {key: 'pickup_datetime', label: 'Pickup Date & Time'},
  {key: 'return_location', label: 'Return Location'},
  {key: 'return_datetime', label: 'Return Date & Time'},
] as const;

const form = reactive<VehicleDetails>(
  Object.fromEntries(fields.map((field) => {
    const value = props.details?.[field.key];

    return [field.key, value === null || value === undefined ? '' : String(value)];
  })),
);

const saving = ref(false);

const save = async (): Promise<void> => {
  saving.value = true;
  try {
    const {data} = await axios.put(
      `/v1/order-documents/${props.documentUuid}/lines/${props.lineUuid}/vehicle-details`,
      {vehicle_details: {...form}},
    );
    emit('saved', data.vehicle_details as VehicleDetails);
    push.success({message: 'Vehicle details updated'});
  } catch {
    push.error({message: 'Failed to update vehicle details'});
  } finally {
    saving.value = false;
  }
};
</script>
